// PWA Service Worker Hook
// Integrates with vite-plugin-pwa for manual updates and PWA functionality

import { useCallback, useEffect, useRef, useState } from 'react';
import { useToastr } from '@farmshare/ui-components';
import { Refresh } from '@mui/icons-material';

interface PWAUpdateProps {
  onNeedRefresh?: () => void;
  onOfflineReady?: () => void;
}

export function usePWA({ onNeedRefresh, onOfflineReady }: PWAUpdateProps = {}) {
  const [needRefresh, setNeedRefresh] = useState(false);
  // const [offlineReady, setOfflineReady] = useState(false);
  const { push } = useToastr();
  const hasShownUpdateToastRef = useRef(false);
  const isUpdatingRef = useRef(false);

  const updateApp = useCallback(() => {
    // Prevent multiple simultaneous updates
    if (isUpdatingRef.current) return;
    isUpdatingRef.current = true;

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistration().then(async (registration) => {
        if (registration?.waiting) {
          // Tell the waiting service worker to skip waiting and become active
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }

        // Clear all caches to ensure fresh content
        if ('caches' in window) {
          const cacheNames = await caches.keys();
          await Promise.all(
            cacheNames.map((cacheName) => caches.delete(cacheName)),
          );
        }
      });
    }

    // Add a small delay to allow cache clearing to complete
    setTimeout(() => {
      window.location.reload();
    }, 500);
  }, []);

  useEffect(() => {
    // Check if PWA plugin has registered update functions
    if (!('serviceWorker' in navigator)) {
      return;
    }

    let updateInterval: NodeJS.Timeout;
    let checkForUpdates: (() => void) | undefined;

    // Listen for PWA update events
    const handleUpdateAvailable = () => {
      setNeedRefresh(true);
      onNeedRefresh?.();

      if (hasShownUpdateToastRef.current) return;
      hasShownUpdateToastRef.current = true;

      // Show toast notification for update
      push({
        id: 'pwa-update',
        severity: 'info',
        title: 'An Update is Available',
        body: 'A new version of the app is available. Please refresh to see the latest changes.',
        delay: 20000,
        action: {
          label: 'Refresh',
          onClick: updateApp,
          startIcon: <Refresh />,
        },
      });
    };

    // Listen for controller change (new SW activated)
    const handleControllerChange = () => {
      // Don't auto-reload on controller change to prevent infinite loops
      // The user should explicitly trigger the refresh via the toast button
      // if (needRefresh) {
      //   window.location.reload();
      // }
    };

    // Check for existing vite-pwa registration
    navigator.serviceWorker.getRegistration().then((registration) => {
      if (registration) {
        // Listen for updates
        registration.addEventListener('updatefound', handleUpdateAvailable);

        // If service worker is waiting (update ready), handle it
        if (registration.waiting) {
          handleUpdateAvailable();
        }

        // Check for updates less frequently to avoid spam (every 5 minutes when page is active)
        checkForUpdates = () => {
          if (
            document.visibilityState === 'visible' &&
            !isUpdatingRef.current
          ) {
            registration.update().catch(() => {
              // Silently fail if update check fails
            });
          }
        };

        // Set up periodic update checks (reduced frequency)
        updateInterval = setInterval(checkForUpdates, 300000); // 5 minutes
        document.addEventListener('visibilitychange', checkForUpdates);
      }
    });

    navigator.serviceWorker.addEventListener(
      'controllerchange',
      handleControllerChange,
    );

    // Cleanup function
    // eslint-disable-next-line consistent-return
    return () => {
      if (updateInterval) {
        clearInterval(updateInterval);
      }
      if (checkForUpdates) {
        document.removeEventListener('visibilitychange', checkForUpdates);
      }
      navigator.serviceWorker.removeEventListener(
        'controllerchange',
        handleControllerChange,
      );
    };
  }, [needRefresh, onNeedRefresh, onOfflineReady, push, updateApp]);

  return {
    needRefresh,
    // offlineReady,
    updateApp,
  };
}
